<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { fetchWithAuth } from '$lib/utils/http/httpUtil';
  import { Info } from 'phosphor-svelte';
  import { popup } from '@skeletonlabs/skeleton';
  import Tooltip from '$lib/components/common/tooltip/Tooltip.svelte';
  import { GetHoverPopupSettings } from '$lib/components/common/tooltip/utils';
  import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';

  export let clientId: string = '';
  export let clientName: string = '';

  const titleTooltipSettings = GetHoverPopupSettings('email_title_info');
  const messageTooltipSettings = GetHoverPopupSettings('email_message_info');

  // Form state management
  let isLoading = false;
  let isExecuting = false;
  let hasExistingConfig = false;
  let wasLoadedFromServer = false;

  // Email setup parameters
  let recipientEmails = '';
  let title = '';
  let message = '';

  function updateFormFromConfig(config: any) {
    recipientEmails = config.recipientEmails || '';
    title = config.title || '';
    message = config.message || '';
  }

  function clearForm() {
    recipientEmails = '';
    title = '';
    message = '';
  }

  $: formIsValid = !!(clientId && recipientEmails);
  $: canSave = formIsValid && !isExecuting;

  onMount(async () => {
    if (clientId) {
      await loadExistingEmailConfig();
    }
  });

  async function loadExistingEmailConfig() {
    if (!clientId || isLoading) return;

    isLoading = true;
    hasExistingConfig = false;

    try {
      const response = await fetchWithAuth(`/api/export/email-config/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.exists) {
          hasExistingConfig = true;
          wasLoadedFromServer = true;

          updateFormFromConfig(result.data.config);

          showToast({
            title: 'Existing Configuration Loaded',
            message: 'This client has existing Email export configuration.',
            type: 'info',
          });
        } else {
          hasExistingConfig = false;
        }
      }
    } catch (error) {
      console.error('Failed to check existing Email config:', error);
      hasExistingConfig = false;
    } finally {
      isLoading = false;
    }
  }

  function createRequestBody() {
    return {
      clientId: parseInt(clientId),
      recipientEmails,
      title: title || null,
      message: message || null,
    };
  }

  async function saveEmailConfiguration() {
    if (!canSave) return;

    isExecuting = true;

    try {
      const requestBody = createRequestBody();

      const response = await fetchWithAuth('/api/export/email-setup', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        hasExistingConfig = true;
        showToast({
          title: 'Success',
          message: 'Email export configuration saved successfully',
          type: 'success',
        });
      } else {
        showToast({
          title: 'Error',
          message: 'Failed to save Email export configuration',
          type: 'error',
        });

        let errorMessage = '';

        if (Array.isArray(result.errors)) {
          errorMessage = result.errors.join(', ');
        } else if (typeof result.errors === 'string') {
          errorMessage = result.errors;
        } else if (result.errors) {
          errorMessage = JSON.stringify(result.errors);
        }

        if (errorMessage) {
          showToast({
            title: 'Error',
            message: errorMessage,
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('Failed to save Email configuration:', error);
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save Email export configuration',
        type: 'error',
      });
    } finally {
      isExecuting = false;
    }
  }

  async function getExportStatus() {
    try {
      const response = await fetchWithAuth(`/api/export/pending-exports/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.summary) {
          showToast({
            title: 'Invoice Export Status',
            message: result.data.summary,
            type: 'info',
          });
        } else {
          showToast({
            title: 'Error',
            message: 'Failed to retrieve invoice export status',
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('Failed to retrieve invoice export state:', error);
      showToast({
        title: 'Error',
        message: 'Failed to retrieve invoice export status:' + (error instanceof Error ? error.message : ''),
        type: 'error',
      });
    }
  }

</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">Email Export Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure email export settings for this client. Exported invoices will be sent to the specified recipients.</p>

  <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <div class="flex items-center justify-between">
      <h4 class="font-semibold">Email Export Configuration</h4>
      {#if isLoading}
        <span class="text-sm text-gray-500">Loading existing configuration...</span>
      {:else if wasLoadedFromServer}
        <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
          <Info size={16} />
          <span>Updating existing configuration</span>
        </div>
      {/if}
    </div>

    {#if hasExistingConfig}
      <div class="rounded-md bg-blue-50 p-3 text-sm text-blue-700">
        <p>Configuring for client: <strong>{clientName}</strong></p>
      </div>
    {:else}
      <div class="rounded-md bg-green-50 p-3 text-sm text-green-700">
        <p>Creating configuration for client: <strong>{clientName}</strong></p>
      </div>
    {/if}

    <div class="space-y-4">
      <div>
        <label for="recipient-emails" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Recipient Emails
          <RequiredBadge />
        </label>
        <textarea
          id="recipient-emails"
          bind:value={recipientEmails}
          placeholder="Enter comma-separated email addresses (e.g. user@example.com, admin@example.com)"
          maxlength="2000"
          rows="3"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={recipientEmails}
          class:border-red-300={!recipientEmails}
          disabled={isExecuting}
        />
        <p class="mt-1 text-xs text-gray-500">Separate multiple email addresses with commas. Maximum 2000 characters.</p>
      </div>

      <div>
        <label for="email-title" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Title
          <button class="[&>*]:pointer-events-none" use:popup={titleTooltipSettings}>
            <Info size={16} />
          </button>
        </label>
        <input
          id="email-title"
          type="text"
          bind:value={title}
          placeholder="Enter email subject/title"
          maxlength="200"
          class="w-full rounded-md border border-gray-300 px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isExecuting}
        />
        <p class="mt-1 text-xs text-gray-500">Optional. Maximum 200 characters.</p>
      </div>

      <div>
        <label for="email-message" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Message
          <button class="[&>*]:pointer-events-none" use:popup={messageTooltipSettings}>
            <Info size={16} />
          </button>
        </label>
        <textarea
          id="email-message"
          bind:value={message}
          placeholder="Enter email body message"
          maxlength="4000"
          rows="5"
          class="w-full rounded-md border border-gray-300 px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isExecuting}
        />
        <p class="mt-1 text-xs text-gray-500">Optional. Maximum 4000 characters.</p>
      </div>
    </div>

    <div class="flex gap-3 justify-end border-t pt-4">
      <Button label="Invoice Export Status" onClick={getExportStatus} disabled={isExecuting} colour="secondary" />
      <Button
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
        colour="primary"
        size="medium"
        onClick={saveEmailConfiguration}
        active={canSave}
      />
    </div>
  </div>
</div>

<Tooltip config={titleTooltipSettings}>
  Use template variables to personalise the subject:<br />
  <strong>{'{{InvoiceNumber}}'}</strong> — actual invoice number<br />
  <strong>{'{{InvoiceClientName}}'}</strong> — client / school name
</Tooltip>

<Tooltip config={messageTooltipSettings}>
  Use template variables to personalise the message body:<br />
  <strong>{'{{InvoiceNumber}}'}</strong> — actual invoice number<br />
  <strong>{'{{InvoiceClientName}}'}</strong> — client / school name
</Tooltip>
